import { Component, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { inject } from '@angular/core';
import { BookingChangesService } from '../_services/booking-changes.service';
import { HttpClient } from '@angular/common/http';
import { HubConnection, HubConnectionBuilder } from '@microsoft/signalr';
import { AuthService } from '@auth0/auth0-angular';
import { environment } from 'src/environments/environment';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { bmxurl, ConvertDateToDB, getAccess } from 'src/globals';
import { BusyService } from '../_services/busy.service';
import { GlobalService } from '../_services/global.service';
import { ItineraryService, itineraryrow } from '../_services/itinerary.service';
import { DaysheetOrderService } from '../_services/daysheet-order.service';
import { users_returndata } from 'src/types/user_returndata';
import { booking_returndata } from 'src/types/booking_returndata';

//The Itinerary Report for a single day. Same sheet, same rows - the service works both of them
//out - with the Day Sheet's date picker at the top in place of the report's date range.
@Component({
  selector: 'app-itineraryview',
  templateUrl: './itineraryview.component.html',
  styleUrls: ['./itineraryview.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ItineraryviewComponent implements OnInit, OnDestroy {

  //Reloads when a booking on screen is changed elsewhere in the Dashboard - the AI assistant
  //moving one, say - since the venue hub may not tell the day a booking was moved away from.
  private bookingChangesSub = inject(BookingChangesService).changes.subscribe(change => {
    if (this.liveRefresh && BookingChangesService.affects(change, this.chosenDate)) {
      this.loadgrid();
    }
  });

  //---- Optional extras, added to be tried out -------------------------------------------------
  //Each one stands alone: set it to false to turn it off, or delete the flag and whatever it
  //guards to remove it for good. Nothing else on the screen depends on any of them.
  showCapacity: boolean = true;      //places used out of the room's capacity, on the slot heading
  showOutstanding: boolean = true;   //money still owed, in red, as the other booking lists show it
  showExtras: boolean = true;        //what the booking has bought that needs preparing
  showAdmitted: boolean = true;      //a tick against bookings already checked in
  liveRefresh: boolean = true;       //reload when a booking on this date changes
  showHeatmap: boolean = true;       //the capacity grid above the table
  //---------------------------------------------------------------------------------------------


  VenueID: string = localStorage.getItem("SmartVenueID")!;
  httpResponse: any;
  AccessAllowed: boolean = false;
  CompletedLoading: boolean = false;

  chosenDate: Date = new Date();

  resources: any[] = [];
  chosenresources: any[] = [];
  //Applied here rather than through the table's own global filter, which would leave the
  //timeslot sub-headings behind - which row starts a slot is worked out before rendering.
  searchText: string = "";
  private resourceOrder = new Map<string, number>();
  private resourceCapacity = new Map<string, number>();
  //Which resources the sheet shows, from the Daysheet Resource Order page - null until it loads.
  private visible: Map<string, boolean> = null;
  //Kept so the rows can be rebuilt when the Daysheet order lands after them.
  private bookings: any[] = [];
  //The resource sections folded shut, by resource id. Kept across a change of date, so a section
  //stays shut while the days are stepped through.
  collapsed = new Set<string>();

  //The capacity grid: a row per resource, a column per time the day uses. Closed to start with,
  //so the sheet itself is what you land on.
  heatmapOpen: boolean = false;
  heatTimes: string[] = [];
  heatRows: any[] = [];

  private hubConnection: HubConnection = null;
  private hubEvent: string = "";

  ListRows: itineraryrow[] = [];
  //Every line for the day, before the resource filter is applied. Kept so changing the filter
  //re-cuts the sheet without going back to the server.
  AllRows: itineraryrow[] = [];

  guests: number = 0;

  cols: any[] = this.itineraryService.cols;

  constructor(
    private http: HttpClient,
    private router: Router,
    private messageService: MessageService,
    private busyService: BusyService,
    private globalService: GlobalService,
    private auth: AuthService,
    private itineraryService: ItineraryService,
    private daysheetOrder: DaysheetOrderService) { }

  //A resource by time grid of how full each room is. Built from the rows already on screen, so
  //it follows the resource filter and the search rather than being a second view of the data.
  private buildHeatmap() {
    if (!this.showHeatmap) {
      this.heatTimes = [];
      this.heatRows = [];
      return;
    }

    //Every time the day actually uses, earliest first.
    this.heatTimes = Array.from(new Set(this.ListRows.map(row => row.timeslot)))
      .sort((one, two) => this.minutesOf(one) - this.minutesOf(two));

    //Resources in the order the rows are already in, which is the Resources screen's order.
    const byResource = new Map<string, any>();

    this.ListRows.forEach(row => {
      if (!byResource.has(row.resourceId)) {
        byResource.set(row.resourceId, {
          resourceId: row.resourceId,
          resourceName: row.resourceName,
          capacity: this.resourceCapacity.get(row.resourceId) ?? 0,
          totals: new Map<string, any>()
        });
      }

      const resource = byResource.get(row.resourceId);
      const cell = resource.totals.get(row.timeslot) ?? { people: 0, bookings: 0, names: [] };

      cell.people += row.people;
      cell.bookings++;
      cell.names.push(row.name || row.bookingIdHR);
      resource.totals.set(row.timeslot, cell);
    });

    this.heatRows = Array.from(byResource.values()).map(resource => ({
      resourceName: resource.resourceName,
      capacity: resource.capacity,
      cells: this.heatTimes.map(time => this.heatCell(resource, time))
    }));
  }

  private heatCell(resource: any, time: string): any {
    const found = resource.totals.get(time);
    const people = found == null ? 0 : found.people;
    const bookings = found == null ? 0 : found.bookings;
    const capacity = resource.capacity;

    return {
      time: time,
      people: people,
      bookings: bookings,
      band: this.heatBand(people, capacity),
      tooltip: this.heatTooltip(resource, time, found, capacity)
    };
  }

  //0 is nothing booked. 1 to 5 climb the blue ramp, 6 is over capacity and -1 is booked against
  //a resource with no capacity set, where a share cannot be worked out and is not invented.
  private heatBand(people: number, capacity: number): number {
    if (people <= 0) {
      return 0;
    }

    if (capacity <= 0) {
      return -1;
    }

    const share = people / capacity;

    if (share > 1) return 6;
    if (share >= 1) return 5;
    if (share >= 0.75) return 4;
    if (share >= 0.5) return 3;
    if (share >= 0.25) return 2;

    return 1;
  }

  private heatTooltip(resource: any, time: string, cell: any, capacity: number): string {
    if (cell == null) {
      return resource.resourceName + " · " + time + " · nothing booked";
    }

    const places = capacity > 0
      ? cell.people + " of " + capacity + " places"
      : cell.people + (cell.people == 1 ? " person" : " people") + " (no capacity set)";

    return resource.resourceName + " · " + time + " · "
      + cell.bookings + (cell.bookings == 1 ? " booking" : " bookings") + " · " + places
      + " — " + cell.names.join(", ");
  }

  private minutesOf(time: string): number {
    if (time == null || time.length < 5) {
      return 0;
    }

    return (Number(time.substring(0, 2)) * 60) + Number(time.substring(3, 5));
  }

  ngOnDestroy(): void {
    this.bookingChangesSub.unsubscribe();
    if (this.hubConnection != null) {
      this.hubConnection.stop();
    }
  }

  ngOnInit(): void {

    this.globalService.setSmallScreenMenu(false);

    let userid = localStorage.getItem("SmartUserID");

    this.http.get(bmxurl + "User/" + userid).subscribe(response => {
      this.httpResponse = (response as users_returndata);

      if (this.httpResponse.succeeded) {
        this.AccessAllowed = getAccess("MAKEABOOKING", this.httpResponse.data);

        //Asked for together. The rows are drawn as soon as the bookings land and are rebuilt
        //when the Daysheet order does, whichever way round that happens.
        this.loadResources();
        this.loadgrid();
        this.startLiveRefresh();
      }

      this.CompletedLoading = true;
    }, error => {
      this.CompletedLoading = true;
      this.messageService.add({severity:'error', summary:'Error', detail:'Error while getting User.'});
    });
  }

  /*
    Which resources the sheet shows and in what order come from the Daysheet Resource Order page,
    the same as the Daysheet and the Itinerary report - see ItineraryService.fromDaysheetOrder. If
    that order cannot be had, each resource's own Show in Itinerary switch decides, in the
    Resources screen's order.
  */
  loadResources() {
    this.daysheetOrder.load(this.VenueID).subscribe(result => {
      const everyResource = result.items.filter(item => item.resource != null).map(item => item.resource);

      if (result.failed) {
        this.resourceOrder = this.itineraryService.orderOf(everyResource);
        //Nothing to go by, so everything is shown rather than nothing.
        this.visible = new Map<string, boolean>();
        this.messageService.add({severity:'warn', summary:'Daysheet order', detail:'The Daysheet Resource Order could not be loaded, so the itinerary is showing every resource.'});
      }
      else {
        const layout = this.itineraryService.fromDaysheetOrder(result.items);
        this.resourceOrder = layout.order;
        this.visible = layout.visible;
      }

      this.resources = this.itineraryService.shownResources(result.items, this.visible);
      this.resourceCapacity = this.itineraryService.capacityOf(everyResource);

      //The bookings may already be drawn - rebuilt so the right resources are on the sheet.
      this.AllRows = this.itineraryService.buildRows(this.bookings, this.resourceOrder, this.visible);
      this.applyResourceFilter();
    }, error => {
      this.messageService.add({severity:'error', summary:'Error', detail:'Error while getting Resources.'});
    });
  }

  addDays(date: Date, days: number): Date {
    const moved = new Date(date);
    moved.setDate(moved.getDate() + days);
    return moved;
  }

  dateChange(days: number) {
    this.chosenDate = this.addDays(this.chosenDate, days);
    this.loadgrid();
    this.watchChosenDate();
  }

  dateSelected() {
    this.loadgrid();
    this.watchChosenDate();
  }

  //Bookings change while this is left open on a back office screen, so the sheet follows the
  //venue's booking events for whichever date is being shown. Failing to connect costs nothing
  //but the automatic reload - Refresh still works.
  private startLiveRefresh() {
    if (!this.liveRefresh) {
      return;
    }

    this.auth.getAccessTokenSilently().subscribe(token => {
      this.hubConnection = new HubConnectionBuilder()
        .withUrl(environment.servicestarget + "/venue/" + this.VenueID, { accessTokenFactory: () => token })
        .build();

      this.hubConnection.start().then(() => this.watchChosenDate()).catch(() => {});
    }, () => {});
  }

  //One event name per date, so moving to another date stops listening to the one before.
  private watchChosenDate() {
    if (!this.liveRefresh || this.hubConnection == null) {
      return;
    }

    const d = new Date(this.chosenDate);
    const event = "bookingEvent_" + d.getFullYear()
      + ('00' + (d.getMonth() + 1)).slice(-2)
      + ('00' + d.getDate()).slice(-2);

    if (event == this.hubEvent) {
      return;
    }

    if (this.hubEvent != "") {
      this.hubConnection.off(this.hubEvent);
    }

    this.hubEvent = event;
    this.hubConnection.on(event, () => this.loadgrid());
  }

  editdata(bookingId: string) {
    this.router.navigateByUrl("/editbooking/" + bookingId);
  }

  //One day, so the range is the chosen date at both ends.
  loadgrid() {
    this.busyService.busy();

    const searchCrit = this.itineraryService.searchCriteria(
      this.VenueID,
      ConvertDateToDB(this.chosenDate),
      ConvertDateToDB(this.chosenDate),
      []);

    this.http.post(bmxurl + "/BookingList", searchCrit).subscribe(response => {
      this.bookings = ((response as booking_returndata).data ?? [])
        .filter((b: any) => b.inProgress == false);

      //Not built until the Daysheet order is in, so a resource switched off there never flashes
      //up first - loadResources builds the rows when it lands.
      this.AllRows = this.visible == null ? [] : this.itineraryService.buildRows(this.bookings, this.resourceOrder, this.visible);
      this.applyResourceFilter();
      this.busyService.idle();
    }, error => {
      this.busyService.idle();
      this.messageService.add({severity:'error', summary:'Error getting bookings', detail:'An error occurred getting Bookings.'});
    });
  }

  /*
    Deliberately does nothing. A PrimeNG table that groups its rows sorts them itself by the
    grouping field - here the resource id, a GUID - which throws away the order the service put
    them in and draws the sections in GUID order instead. Marking the table customSort hands
    that sort to this method, and leaving the rows alone is the sort that is wanted: they
    arrive already in the Daysheet order.
  */
  keepOrder() { }

  applyResourceFilter() {
    const arranged = this.itineraryService.arrange(
      this.AllRows,
      this.chosenresources,
      this.resourceOrder,
      this.searchText,
      this.showCapacity ? this.resourceCapacity : null);

    this.ListRows = arranged.rows;
    this.guests = arranged.guests;

    //After the rows are in place, not before - the grid is built from them.
    this.buildHeatmap();
  }

  //A section heading's name with the resource's concurrent uses in brackets - just the name for
  //a resource the resource list does not know.
  sectionTitle(row: itineraryrow): string {
    const capacity = this.itineraryService.capacityFor(this.resourceCapacity, row);

    return row.resourceName + (capacity == null ? "" : " (" + capacity + ")");
  }

  toggleSection(row: itineraryrow) {
    if (this.collapsed.has(row.resourceId)) {
      this.collapsed.delete(row.resourceId);
    }
    else {
      this.collapsed.add(row.resourceId);
    }
  }

  /*
    Every row the sheet is showing, folded sections included, with the resource's capacity
    alongside its name. Done here rather than by the table's own export so the capacity can go
    in and the file gets a proper name rather than download.csv - as the Itinerary Report does.
  */
  exportCsv() {
    const cols = [...this.cols];
    cols.splice(cols.findIndex(col => col.field == 'resourceName') + 1, 0, { field: 'capacity', header: 'Capacity' });

    const valueOf = (row: itineraryrow, field: string): string => {
      if (field == 'capacity') {
        const capacity = this.itineraryService.capacityFor(this.resourceCapacity, row);
        return capacity == null ? "" : String(capacity);
      }

      return String((row as any)[field] ?? "");
    };

    const cell = (value: string): string => '"' + value.replace(/"/g, '""') + '"';
    const lines = [cols.map(col => cell(col.header)).join(",")]
      .concat((this.ListRows ?? []).map(row => cols.map(col => cell(valueOf(row, col.field))).join(",")));

    //The byte order mark is what makes Excel read the pound sign and accents correctly.
    const blob = new Blob(["\ufeff" + lines.join("\r\n")], { type: 'text/csv;charset=utf-8' });
    const link = document.createElement("a");

    link.href = URL.createObjectURL(blob);
    link.download = "Itinerary_" + this.fileDate(this.chosenDate) + ".csv";
    link.click();

    URL.revokeObjectURL(link.href);
  }

  //The picker's own day, read locally, so the file is named for the date on screen.
  private fileDate(value: Date): string {
    const date = new Date(value);
    const pad = (n: number) => String(n).padStart(2, "0");

    return date.getFullYear() + "-" + pad(date.getMonth() + 1) + "-" + pad(date.getDate());
  }
}
